import { Link } from "react-router-dom";
import { useAuthStore } from "../stores/auth";
import { blueprint } from "../blueprint";
import type { NavGroup, WebModule } from "../modules";
import { PageHeader } from "../components/ui/page-header";
import { Card } from "../components/ui/card";
import { Icon } from "../components/ui/icon";

/** Même tri que la barre latérale : par rang, puis dans l'ordre des modules. */
function sortedGroups(modules: WebModule[]): NavGroup[] {
  return modules
    .flatMap((m) => m.nav)
    .map((g, index) => ({ g, index, order: g.order ?? 50 }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ g }) => g);
}

/**
 * Accueil : salutation, widgets des modules (les « larges » d'abord — c'est le
 * tableau du jour qu'on veut voir en ouvrant l'outil), puis les raccourcis de
 * navigation, utiles surtout sur téléphone où la barre latérale est repliée.
 */
export function Home({ modules }: { modules: WebModule[] }) {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const widgets = modules
    .flatMap((m) => m.homeWidgets ?? [])
    .filter((w) => !w.adminOnly || isAdmin)
    .map((w, index) => ({ w, index, wide: w.size === "wide" ? 0 : 1 }))
    .sort((a, b) => a.wide - b.wide || a.index - b.index)
    .map(({ w }) => w);
  const links = sortedGroups(modules)
    .filter((g) => !g.adminOnly || isAdmin)
    .flatMap((g) => g.items.filter((i) => !i.adminOnly || isAdmin));

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";

  return (
    <div className="home">
      <PageHeader
        title={`${greeting}, ${user?.name ?? ""}`}
        subtitle={blueprint.tagline || blueprint.name}
      />

      <section className="home__widgets">
        {widgets.map((w) => (
          <div
            key={w.id}
            className={`home__widget${w.size === "wide" ? " home__widget--wide" : ""}`}
          >
            {w.element}
          </div>
        ))}
        {widgets.length === 0 && links.length === 0 ? (
          <Card title="Bienvenue">
            <p className="home__empty">
              Aucun module n'est encore activé. Cochez-en dans <code>blueprint.json</code> puis
              lancez <code>pnpm blueprint apply</code>.
            </p>
          </Card>
        ) : null}
      </section>

      {links.length > 0 ? (
        <section className="home__shortcuts-block" aria-labelledby="home-shortcuts-title">
          <h2 className="home__shortcuts-title" id="home-shortcuts-title">
            Accès rapides
          </h2>
          <div className="home__shortcuts">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="home__shortcut">
                <span className="home__shortcut-icon">
                  <Icon name={l.icon} size={20} />
                </span>
                <span className="home__shortcut-label">{l.label}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
